import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useMessages, useSendMessage, useUploadAttachment } from "@/hooks/useMessages";
import { useMarkThreadAsRead } from "@/hooks/useUnreadCounts";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Send, Paperclip, X, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface MessageThreadViewProps {
  threadId: string;
  participantNames?: Record<string, string>;
}

export const MessageThreadView = ({ threadId, participantNames = {} }: MessageThreadViewProps) => {
  const { user } = useAuth();
  const { data: messages, isLoading } = useMessages(threadId);
  const sendMessage = useSendMessage();
  const uploadAttachment = useUploadAttachment();
  const markAsRead = useMarkThreadAsRead();
  
  const [messageText, setMessageText] = useState("");
  const [pendingAttachments, setPendingAttachments] = useState<string[]>([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sender IDs that appear in messages and don't have a name from parent
  const missingSenderIds = useMemo(() => {
    if (!messages) return [];
    const ids = new Set<string>();
    messages.forEach((m) => {
      if (m.sender_user_id && m.sender_user_id !== user?.id && !participantNames[m.sender_user_id]) {
        ids.add(m.sender_user_id);
      }
    });
    return Array.from(ids);
  }, [messages, user?.id, participantNames]);

  // Resolve display names for senders not in participantNames (profiles + cleaners)
  const { data: resolvedNames = {} } = useQuery({
    queryKey: ["message-sender-names", missingSenderIds],
    queryFn: async () => {
      if (missingSenderIds.length === 0) return {};
      const [profilesRes, cleanersRes] = await Promise.all([
        supabase.from("profiles").select("id, full_name, first_name, last_name").in("id", missingSenderIds),
        supabase.from("cleaners").select("user_id, name").in("user_id", missingSenderIds),
      ]);
      const names: Record<string, string> = {};
      profilesRes.data?.forEach((p: { id: string; full_name?: string | null; first_name?: string | null; last_name?: string | null }) => {
        const full = (p.full_name ?? "").trim();
        const first = (p.first_name ?? "").trim();
        const last = (p.last_name ?? "").trim();
        const combined = full || [first, last].filter(Boolean).join(" ").trim();
        if (combined) names[p.id] = combined;
      });
      cleanersRes.data?.forEach((c: { user_id: string; name?: string | null }) => {
        if (c.user_id && (c.name ?? "").trim()) names[c.user_id] = (c.name ?? "").trim();
      });
      return names;
    },
    enabled: missingSenderIds.length > 0,
  });

  const allNames = useMemo(
    () => ({ ...participantNames, ...resolvedNames }),
    [participantNames, resolvedNames]
  );

  // Mark thread as read when opened and when messages change
  useEffect(() => {
    if (threadId && messages) {
      markAsRead.mutate(threadId);
    }
  }, [threadId, messages?.length]);

  // Smooth scroll to bottom when messages change
  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    messagesEndRef.current?.scrollIntoView({ behavior, block: "end" });
  }, []);

  useEffect(() => {
    // Use instant scroll on initial load, smooth scroll for new messages
    const behavior = messages && messages.length <= 1 ? "instant" : "smooth";
    scrollToBottom(behavior as ScrollBehavior);
  }, [messages, scrollToBottom]);

  const handleSend = async () => {
    if (!messageText.trim() && pendingAttachments.length === 0) return;

    try {
      await sendMessage.mutateAsync({
        threadId,
        messageText: messageText.trim(),
        attachments: pendingAttachments,
      });
      setMessageText("");
      setPendingAttachments([]);
      // Scroll to bottom after sending
      setTimeout(() => scrollToBottom("smooth"), 100);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      const url = await uploadAttachment.mutateAsync({ file, threadId });
      setPendingAttachments((prev) => [...prev, url]);
    } catch (error) {
      console.error("Error uploading file:", error);
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const removeAttachment = (index: number) => {
    setPendingAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const getSenderName = (senderId: string) => {
    if (senderId === user?.id) return "You";
    return allNames[senderId] || "Unknown";
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 p-4 space-y-4 overflow-hidden">
          <Skeleton className="h-16 w-3/4" />
          <Skeleton className="h-16 w-1/2 ml-auto" />
          <Skeleton className="h-16 w-2/3" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Messages List - Scrollable area */}
      <div 
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto overscroll-contain px-4 py-3"
        style={{ 
          WebkitOverflowScrolling: "touch",
          scrollBehavior: "smooth"
        }}
      >
        {messages && messages.length > 0 ? (
          <div className="space-y-3 min-h-full flex flex-col justify-end">
            {messages.map((message) => {
              const isOwn = message.sender_user_id === user?.id;
              const isSystem = message.system_flag;

              if (isSystem) {
                return (
                  <div key={message.id} className="flex justify-center py-1">
                    <span className="text-[11px] text-muted-foreground bg-muted/80 px-3 py-1.5 rounded-full max-w-[85%] text-center">
                      {message.message_text}
                    </span>
                  </div>
                );
              }

              return (
                <div
                  key={message.id}
                  className={cn(
                    "flex flex-col max-w-[88%] min-w-0",
                    isOwn ? "ml-auto items-end" : "items-start"
                  )}
                >
                  <span className="text-[11px] text-muted-foreground mb-0.5 px-1">
                    {getSenderName(message.sender_user_id)}
                  </span>
                  <div
                    className={cn(
                      "rounded-2xl px-3.5 py-2 shadow-sm",
                      isOwn
                        ? "bg-primary text-primary-foreground rounded-br-md"
                        : "bg-muted text-foreground rounded-bl-md"
                    )}
                  >
                    <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">
                      {message.message_text}
                    </p>
                    
                    {/* Attachments */}
                    {message.attachments && message.attachments.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {message.attachments.map((url, i) => (
                          <a
                            key={i}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block touch-manipulation"
                          >
                            <img
                              src={url}
                              alt="Attachment"
                              className="max-w-[180px] sm:max-w-[200px] max-h-[140px] rounded-lg object-cover hover:opacity-90 active:opacity-80 transition-opacity"
                            />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="text-[10px] text-muted-foreground mt-0.5 px-1">
                    {format(new Date(message.created_at), "MMM d, h:mm a")}
                  </span>
                </div>
              );
            })}
            {/* Scroll anchor */}
            <div ref={messagesEndRef} className="h-1" />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center py-8">
            <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-3">
              <Send className="w-6 h-6 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-muted-foreground">No messages yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Start the conversation!
            </p>
          </div>
        )}
      </div>

      {/* Pending Attachments Preview */}
      {pendingAttachments.length > 0 && (
        <div className="px-3 py-2 border-t bg-muted/30 shrink-0">
          <div className="flex flex-wrap gap-2">
            {pendingAttachments.map((url, i) => (
              <div key={i} className="relative">
                <img
                  src={url}
                  alt="Pending"
                  className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg object-cover"
                />
                <button
                  onClick={() => removeAttachment(i)}
                  className="absolute -top-1 -right-1 w-5 h-5 sm:w-4 sm:h-4 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center shadow touch-manipulation"
                >
                  <X className="w-2.5 h-2.5 sm:w-2 sm:h-2" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Input Area - Fixed at bottom with safe area padding */}
      <div 
        className="shrink-0 border-t bg-card/95 backdrop-blur-sm"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="p-3 sm:p-4">
          <div className="flex items-center gap-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept="image/*"
              className="hidden"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingFile}
              className="shrink-0 h-10 w-10 touch-manipulation"
            >
              {uploadingFile ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Paperclip className="w-5 h-5" />
              )}
            </Button>
            <Input
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              className="flex-1 h-10 text-base sm:text-sm"
              autoComplete="off"
              autoCorrect="on"
            />
            <Button
              type="button"
              size="icon"
              onClick={handleSend}
              disabled={sendMessage.isPending || (!messageText.trim() && pendingAttachments.length === 0)}
              className="shrink-0 h-10 w-10 touch-manipulation"
            >
              {sendMessage.isPending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
